import React, { useEffect } from 'react'
import {
  Table, Button, Card, Form, Input, Select, message, Drawer, Space, Tooltip, Popconfirm, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, BankOutlined, CheckCircleOutlined, SearchOutlined,
} from '@ant-design/icons'
import { useMasterData, type Edc } from '@/context/MasterDataContext'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { generateCode } from '@/utils/codeGenerator'
import { ApiError } from '@/services/api'
import './MasterForm.css'

const BASE = '/master/edcs'
const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const EdcMaster: React.FC = () => {
  const { edcs, deleteEdc, saveEdc, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [viewRecord, setViewRecord] = React.useState<Edc | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = React.useState(false)

  /* List search / filter */
  const [searchText, setSearchText] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState('')

  const activeCount = edcs.filter(e => e.status === 'active').length

  const filteredEdcs = edcs.filter(e => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      e.name.toLowerCase().includes(q) ||
      (e.place || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || e.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
    } else if (mode === 'edit') {
      const rec = edcs.find(e => e.id === editId)
      if (rec) form.setFieldsValue(rec)
      else goList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, edcs])

  const handleDelete = async (id: string) => {
    try {
      await deleteEdc(id)
      message.success('EDC removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    let id = editId
    if (!isEdit) {
      try {
        id = await generateCode('edcs')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate an EDC code — check your connection and try again.")
        return
      }
    }
    const payload = { ...values, id }
    setSubmitting(true)
    try {
      await saveEdc(payload, isEdit)
      message.success(isEdit ? 'EDC updated' : 'EDC added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Code', dataIndex: 'id', width: 110,
      render: (v: string) => <span style={{ ...MONO, fontWeight: 700, color: '#1677ff', fontSize: 13 }}>{v}</span>,
    },
    {
      title: 'EDC Name', dataIndex: 'name',
      render: (v: string) => <span style={{ fontWeight: 600, color: '#1677ff' }}>{v}</span>,
    },
    {
      title: 'Place / District', dataIndex: 'place',
      render: (v: string) => <span style={{ color: '#4A5568', fontSize: 13 }}>{v || '—'}</span>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: Edc) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit EDC' : 'Add EDC'}
        subtitle="Electricity Distribution Circle"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 560 }}>
          <Form.Item name="name" label="EDC Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Chennai North EDC" />
          </Form.Item>
          <Form.Item name="place" label="Place / District" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Chennai" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="active">
            <Select options={[
              { value: 'active',   label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update EDC' : 'Add EDC'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={5} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BankOutlined style={{ color: '#001A4D' }} /> EDC Master
          </h2>
          <p className="page-subtitle">Electricity Distribution Circles — referenced in Generator and Consumer records</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add EDC</Button>
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total EDCs', value: edcs.length, icon: <BankOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
        ]}
      />

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by name or place..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredEdcs}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} records` }}
        />
      </Card>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{viewRecord?.name}</div>
            <div style={{ fontSize: 12, color: '#8A93A6', ...MONO }}>{viewRecord?.id}</div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={380}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (viewRecord) goEdit(viewRecord.id); setViewRecord(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this record?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(viewRecord!.id); setViewRecord(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {viewRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>
                {viewRecord.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
            <DetailRow label="Code"           value={<span style={MONO}>{viewRecord.id}</span>} />
            <DetailRow label="EDC Name"       value={<strong>{viewRecord.name}</strong>} />
            <DetailRow label="Place / District" value={viewRecord.place} />
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default EdcMaster
